import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { serverTimestamp } from 'firebase/firestore';
import { useAppContext } from '../components/Layout';
import Spinner from '../components/Spinner';
import Drawer from '../components/Drawer';
import RefBadge from '../components/RefBadge';
import ActionActivityFeed from '../components/ActionActivityFeed';
import ActionForm, { type ActionDraft } from '../components/ActionForm';
import {
  useVisibleActions,
  useVisibleContacts,
  useAssignablePeople,
  usePersonLabels,
  useVisibleGoals,
  useVisibleInitiatives,
  useVisibleTasks,
} from '../hooks/useScopedData';
import { useVisibleUnits } from '../hooks/useUnits';
import {
  canCommentOnAction,
  canCreateAction,
  canEditActionFully,
  isAdmin,
  hasCouncilFullRead,
  memberAssignedToTask,
} from '../lib/visibility';
import { actionDueDateDisplay, actionIsDueSoon, actionIsOverdue, actionTypeKey } from '../lib/actions';
import {
  WORK_ITEM_TYPE_FILTER_ORDER,
  actionToWorkItem,
  compareWorkItems,
  initiativeTaskToWorkItem,
  relatedBadgesForAction,
  taskOwnerLabel,
  workItemClickTarget,
  workItemDueDateDisplay,
  workItemSearchText,
  workItemTypeLabel,
  type WorkItem,
} from '../lib/workItems';
import { addActionNote, createAction, updateActionWithActivity } from '../lib/db';
import { PersonMultiPicker, PersonPicker } from '../components/PersonPicker';
import { contactToPersonFrom, isMe, ownerPersonOf, participantPersonsOf } from '../lib/personRefs';
import {
  ACTION_PRIORITY_LABELS,
  ACTION_PRIORITY_ORDER,
  ACTION_STATUS_LABELS,
  ACTION_STATUS_ORDER,
  actionIsResolved,
  recName,
  type Action,
  type ActionPriority,
  type ActionStatus,
  type AppSettings,
  type Contact,
  type PersonOption,
  type Goal,
  type Initiative,
  type Unit,
  type UserProfile,
} from '../types';

type Tab = 'mine' | 'created' | 'district' | 'all';

export default function Actions() {
  const { profile, settings, districtFilter } = useAppContext();
  const actionsQ = useVisibleActions(profile);
  const contactsQ = useVisibleContacts(profile);
  const peopleQ = useAssignablePeople(profile);
  const unitsQ = useVisibleUnits(profile);
  const goalsQ = useVisibleGoals(profile);
  const initiativesQ = useVisibleInitiatives(profile);
  // Initiative Tasks join the workspace here — useVisibleTasks is the SAME
  // hook, with the SAME district-scoped-query security boundary, GoalsTasks
  // and Initiatives already use; reusing it (rather than a new query) is
  // what keeps this aggregation from either weakening authorization or
  // loading more than a user's already-authorized scope. See
  // docs/actions-workspace.md for the full architecture note.
  const tasksQ = useVisibleTasks(profile);
  const location = useLocation();
  const navigate = useNavigate();

  const email = profile.email.toLowerCase();
  const myContactId = profile.contactId ?? null;
  const myPersonId = profile.personId ?? null;
  const canSeeAll = isAdmin(profile) || hasCouncilFullRead(profile);

  const [tab, setTab] = useState<Tab>('mine');
  const [statusFilter, setStatusFilter] = useState<'active' | 'all' | ActionStatus>('active');
  const [priorityFilter, setPriorityFilter] = useState<'all' | ActionPriority>('all');
  const [typeFilter, setTypeFilter] = useState<'all' | string>('all');
  const [includeUnassigned, setIncludeUnassigned] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const [detail, setDetail] = useState<Action | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Deep link from Unit Health's "Action open" indicator (or anywhere else
  // that wants to jump straight to one Action) — same one-shot
  // router-state pattern as Org Chart's "Give login access" -> Settings
  // flow. Consumed once the target Action loads, then cleared from history
  // state so a refresh/back doesn't reopen it.
  const openActionId = (location.state as { openActionId?: string } | null)?.openActionId ?? null;
  useEffect(() => {
    if (!openActionId) return;
    const found = actionsQ.data.find((a) => a.id === openActionId);
    if (found) {
      setDetail(found);
      navigate(location.pathname, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openActionId, actionsQ.data]);

  // Deep link from an Action-assigned notification email (?action=<id>) —
  // same shape as GoalsTasks.tsx/Initiatives.tsx's existing `?task=<id>`
  // handling. Reusable by any Action-based automation (SYT now;
  // Advancement/Renewal/Hazardous Weather later) since they all link to
  // functions/src/systemActions.ts's actionUrl(), which points here.
  const [searchParams, setSearchParams] = useSearchParams();
  const openedActionFromLink = useRef(false);
  useEffect(() => {
    const wantedId = searchParams.get('action');
    if (!wantedId || openedActionFromLink.current || actionsQ.loading) return;
    openedActionFromLink.current = true;
    const found = actionsQ.data.find((a) => a.id === wantedId);
    if (found) setDetail(found);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('action');
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, actionsQ.loading, actionsQ.data]);

  const contactsById = useMemo(() => {
    const m = new Map<string, Contact>();
    for (const c of contactsQ.data) m.set(c.id, c);
    return m;
  }, [contactsQ.data]);
  const contactName = (id: string | null | undefined) => (id ? (contactsById.get(id)?.name ?? 'Unknown') : null);
  // People are the working identity: owners and participants are Person ids, named from the picker list or, for
  // someone outside the viewer's scope, from the server's label service. A legacy Contact id still resolves.
  const toPerson = useMemo(() => contactToPersonFrom(contactsQ.data), [contactsQ.data]);
  const referencedPeople = useMemo(() => {
    const ids: string[] = [];
    for (const a of actionsQ.data) { const o = ownerPersonOf(a, toPerson); if (o) ids.push(o); ids.push(...participantPersonsOf(a, toPerson)); if (a.relatedPersonId) ids.push(a.relatedPersonId); }
    return ids;
  }, [actionsQ.data, toPerson]);
  const labelFor = usePersonLabels(referencedPeople, peopleQ.byId);
  const personName = (personId: string | null | undefined): string | null => (personId ? (labelFor(personId) ?? 'Unknown') : null);
  const ownerName = (a: Action): string | null => {
    const p = ownerPersonOf(a, toPerson);
    return p ? personName(p) : contactName(a.ownerContactId);
  };
  const dName = (id: string) => recName(settings.districts, id);

  const initiativesById = useMemo(() => {
    const m = new Map<string, Initiative>();
    for (const i of initiativesQ.data) m.set(i.id, i);
    return m;
  }, [initiativesQ.data]);

  // Only Initiative Tasks join the workspace — an Initiative itself is NOT
  // actionable work (it's where work is planned/organized), and Goal-
  // sourced/standalone tasks are out of scope for this pass (the request
  // was specifically "Individual Tasks belonging to an Initiative").
  const initiativeTasks = useMemo(
    () => tasksQ.data.filter((t) => t.source === 'initiative' && t.initiativeId),
    [tasksQ.data],
  );

  // The one normalized list the rest of this page reads — built by
  // NORMALIZING existing Action/Task docs (lib/workItems.ts), never by
  // writing a new record. There is still exactly one canonical Action doc
  // and exactly one canonical Task doc each; nothing here can drift from
  // either, because nothing here is persisted.
  const allWorkItems = useMemo<WorkItem[]>(() => {
    const items = actionsQ.data.map(actionToWorkItem);
    for (const t of initiativeTasks) {
      items.push(initiativeTaskToWorkItem(t, initiativesById.get(t.initiativeId as string), settings.taskStatuses));
    }
    return items;
  }, [actionsQ.data, initiativeTasks, initiativesById, settings.taskStatuses]);

  // "Assigned to me" for a WorkItem: an Action's single required owner, or
  // (reusing lib/visibility.ts's existing, already-tested check — not
  // reimplemented) a Task assigned to my Position or directly to me. An
  // Action is never "unassigned" (ownerContactId is required), so this
  // naturally excludes every unassigned Task without any extra guard.
  const workItemAssignedToMe = (w: WorkItem): boolean =>
    w.kind === 'action' ? isMe(profile, w.action!.ownerPersonId, w.action!.ownerContactId) : !!w.task && memberAssignedToTask(profile, w.task);
  const workItemCreatedByMe = (w: WorkItem): boolean => w.createdBy === email;

  // Personal summary strip — always "assigned to me", independent of the
  // currently selected tab, so it's a stable read of your own workload.
  // Deliberately does NOT respect Status/Priority/Type/District/Include-
  // unassigned filters either, for the same reason — it's your whole
  // workload at a glance, not a summary of whatever the table below
  // happens to be showing. An unassigned Task can never be "assigned to
  // me" by definition, so it's already excluded here regardless of
  // Include-unassigned — that toggle has no effect on this strip.
  const mineWorkItems = useMemo(() => allWorkItems.filter(workItemAssignedToMe), [allWorkItems, myContactId, myPersonId, profile]);
  const mineActive = mineWorkItems.filter((w) => !w.isResolved);
  const overdueCount = mineActive.filter((w) => w.isOverdue).length;
  const dueSoonCount = mineActive.filter((w) => w.isDueSoon).length;
  // "Open" keeps its existing exact meaning for a canonical Action
  // (status literally 'open', not just any active status) — unchanged
  // from before this feature. A Task has no equivalent fine-grained
  // status vocabulary (its statuses are per-council configurable), so an
  // assigned, active Task counts toward Open simply by being active,
  // matching the feature request's own example ("Active assigned Task ->
  // Open") without inventing a fake 'open'-equivalent Task status.
  const openCount = mineActive.filter((w) => (w.kind === 'action' ? w.action!.status === 'open' : true)).length;

  const inTopBarScope = (w: WorkItem) => districtFilter === 'All' || w.districtId === districtFilter;

  // Moved above baseList/filtered (unchanged) so the search-text map below
  // can build on the same shared, pure relatedBadgesForAction() rather than
  // duplicating unit/goal/initiative resolution in two places — see
  // lib/workItems.ts.
  const relatedBadges = (a: Action) => relatedBadgesForAction(a, unitsQ.data, goalsQ.data, initiativesQ.data);

  // One lowercased search haystack per WorkItem, built once per data change
  // rather than per keystroke — see workItemSearchText()'s own doc comment
  // in lib/workItems.ts for exactly which fields are included and why.
  const searchTextByKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const w of allWorkItems) {
      map.set(
        w.key,
        workItemSearchText(w, {
          contactName,
          personName,
          contacts: contactsQ.data,
          units: unitsQ.data,
          goals: goalsQ.data,
          initiatives: initiativesQ.data,
        }),
      );
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allWorkItems, contactsById, contactsQ.data, peopleQ.byId, unitsQ.data, goalsQ.data, initiativesQ.data]);

  const baseList = useMemo(() => {
    switch (tab) {
      case 'mine':
        return mineWorkItems;
      case 'created':
        return allWorkItems.filter(workItemCreatedByMe);
      case 'district':
        return allWorkItems.filter((w) => w.districtId === profile.districtId);
      case 'all':
        return allWorkItems;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, mineWorkItems, allWorkItems, email, profile.districtId]);

  const filtered = useMemo(
    () =>
      baseList
        .filter(inTopBarScope)
        .filter((w) => {
          if (statusFilter === 'active') return !w.isResolved;
          if (statusFilter === 'all') return true;
          // A specific ActionStatus value (open/inProgress/waiting/
          // completed/cancelled) has no honest equivalent on a Task —
          // Task's status vocabulary is a different, per-council
          // configurable set (see docs/actions-workspace.md) — so Tasks
          // are excluded rather than guessing a mapping.
          return w.kind === 'action' && w.action!.status === statusFilter;
        })
        .filter((w) => priorityFilter === 'all' || w.priority === priorityFilter)
        .filter((w) => typeFilter === 'all' || w.typeKey === typeFilter)
        .filter((w) => w.kind === 'action' || !w.isUnassigned || includeUnassigned)
        .filter((w) => {
          const q = searchQuery.trim().toLowerCase();
          return !q || (searchTextByKey.get(w.key) ?? '').includes(q);
        })
        .sort(compareWorkItems),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [baseList, districtFilter, statusFilter, priorityFilter, typeFilter, includeUnassigned, searchQuery, searchTextByKey],
  );

  const loading = actionsQ.loading || contactsQ.loading || peopleQ.loading || tasksQ.loading || initiativesQ.loading;

  const save = async (draft: ActionDraft) => {
    setErr(null);
    try {
      const requestedParticipants = draft.participantPersonIds;
      const actionId = await createAction({
        title: draft.title,
        description: draft.description,
        priority: draft.priority,
        districtId: draft.districtId,
        ownerPersonId: draft.ownerPersonId,
        subcommitteeId: draft.subcommitteeId,
        dueDate: draft.dueDate,
        relatedUnitId: draft.relatedUnitId,
        relatedPersonId: draft.relatedPersonId,
        relatedGoalId: draft.relatedGoalId,
        relatedInitiativeId: draft.relatedInitiativeId,
      });
      // Creation starts with no participants so Rules can validate every later participant addition against a real
      // person in this Action's tenant/district. Add the requested people one at a time.
      let participantIds: string[] = [];
      for (const personId of requestedParticipants) {
        participantIds = [...participantIds, personId];
        await updateActionWithActivity(
          actionId,
          { participantPersonIds: participantIds },
          [`added ${personName(personId) ?? 'Unknown'} as a participant.`],
          email,
        );
      }
      setCreating(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not create that Action.');
    }
  };

  return (
    <div className="card">
      <div className="row">
        <div>
          <h2>Actions</h2>
          <div className="action-tagline">Action Changes Things.</div>
          <div className="subtitle">Turn plans and issues into accountable work.</div>
        </div>
        {canCreateAction(profile) && (
          <button className="btn primary" onClick={() => setCreating(true)}>
            + New Action
          </button>
        )}
      </div>

      <div className="action-summary-strip">
        <div className={`action-summary-tile${overdueCount > 0 ? ' is-alert' : ''}`}>
          <b>{overdueCount}</b>
          <span className="subtitle">My Overdue</span>
        </div>
        <div className={`action-summary-tile${dueSoonCount > 0 ? ' is-warn' : ''}`}>
          <b>{dueSoonCount}</b>
          <span className="subtitle">My Due Soon</span>
        </div>
        <div className="action-summary-tile">
          <b>{openCount}</b>
          <span className="subtitle">My Open</span>
        </div>
      </div>

      <div className="uh-leaderchips" style={{ marginTop: 12 }}>
        <button className={`uh-chip${tab === 'mine' ? ' is-on' : ''}`} onClick={() => setTab('mine')}>
          Assigned to Me
        </button>
        <button className={`uh-chip${tab === 'created' ? ' is-on' : ''}`} onClick={() => setTab('created')}>
          Created by Me
        </button>
        <button className={`uh-chip${tab === 'district' ? ' is-on' : ''}`} onClick={() => setTab('district')}>
          My District
        </button>
        {canSeeAll && (
          <button className={`uh-chip${tab === 'all' ? ' is-on' : ''}`} onClick={() => setTab('all')}>
            All Actions
          </button>
        )}
      </div>

      <div className="action-filter-bar">
        <div className="action-search">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search title, unit, owner, description…"
            aria-label="Search Actions"
          />
          {searchQuery && (
            <button
              type="button"
              className="action-search-clear"
              aria-label="Clear search"
              onClick={() => setSearchQuery('')}
            >
              ×
            </button>
          )}
        </div>
        <label>
          <span className="subtitle">Status</span>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}>
            <option value="active">Active (not resolved)</option>
            <option value="all">All statuses</option>
            {ACTION_STATUS_ORDER.map((s) => (
              <option key={s} value={s}>
                {ACTION_STATUS_LABELS[s]} only
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="subtitle">Priority</span>
          <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value as typeof priorityFilter)}>
            <option value="all">All priorities</option>
            {ACTION_PRIORITY_ORDER.map((p) => (
              <option key={p} value={p}>
                {ACTION_PRIORITY_LABELS[p]}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="subtitle">Type</span>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="all">All types</option>
            {WORK_ITEM_TYPE_FILTER_ORDER.map((t) => (
              <option key={t} value={t}>
                {workItemTypeLabel(t)}
              </option>
            ))}
          </select>
        </label>
        <label
          className={tab === 'mine' ? 'is-disabled' : undefined}
          title={tab === 'mine' ? "Unassigned Initiative Tasks can never be \"assigned to me\" — this has no effect in that scope." : undefined}
        >
          <input
            type="checkbox"
            checked={includeUnassigned}
            disabled={tab === 'mine'}
            onChange={(e) => setIncludeUnassigned(e.target.checked)}
          />
          <span className="subtitle">Include unassigned</span>
        </label>
      </div>

      {loading && (
        <div className="empty">
          <Spinner label="Loading…" />
        </div>
      )}
      {err && <div className="err">{err}</div>}
      {actionsQ.error && <div className="err">{actionsQ.error.message}</div>}

      {!loading && (
        <div style={{ overflowX: 'auto', marginTop: 10 }}>
          <table>
            <thead>
              <tr>
                <th>Title</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Owner</th>
                <th>Due</th>
                <th>Related</th>
                {(districtFilter === 'All' || tab === 'all') && <th>District</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((w) => {
                const due = workItemDueDateDisplay(w);
                const open = () => {
                  const target = workItemClickTarget(w);
                  if (target.kind === 'openAction') setDetail(target.action);
                  else navigate(target.path);
                };
                const badges = w.kind === 'action' ? relatedBadges(w.action as Action) : w.relatedLabel ? [w.relatedLabel] : [];
                const ownerText =
                  w.kind === 'action' ? (ownerName(w.action as Action) ?? '—') : taskOwnerLabel(w.task!, contactsQ.data, (id) => personName(id));
                return (
                  <tr key={w.key} onClick={open} style={{ cursor: 'pointer' }}>
                    <td>
                      <button type="button" className="action-title-link" onClick={open}>
                        {w.title}
                      </button>{' '}
                      <RefBadge value={w.ref} />
                      {w.kind === 'initiativeTask' && (
                        <span className="action-related-tag" title="A canonical Initiative Task, not a duplicated Action">
                          Initiative Task
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={`badge${w.isResolved ? ' neutral' : ''}`}>{w.statusLabel}</span>
                    </td>
                    <td>{w.priority ? ACTION_PRIORITY_LABELS[w.priority] : '—'}</td>
                    <td>{ownerText}</td>
                    <td>
                      {due.text}
                      {due.context && (
                        <span
                          className={`action-due-context${w.isOverdue ? ' is-overdue' : w.isDueSoon ? ' is-soon' : ''}`}
                        >
                          {due.context}
                        </span>
                      )}
                    </td>
                    <td>
                      {badges.length === 0
                        ? '—'
                        : badges.map((b, i) => (
                            <span key={i} className="action-related-tag">
                              {b}
                            </span>
                          ))}
                    </td>
                    {(districtFilter === 'All' || tab === 'all') && <td>{dName(w.districtId)}</td>}
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={districtFilter === 'All' || tab === 'all' ? 7 : 6} className="empty">
                    {baseList.length === 0
                      ? 'No work items here.'
                      : searchQuery.trim()
                        ? 'No work items match your search.'
                        : 'No work items match the selected filters.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <ActionForm
          districts={settings.districts}
          people={peopleQ.options}
          labelFor={(id) => labelFor(id)}
          onAddPerson={() => navigate('/contacts')}
          units={unitsQ.data}
          goals={goalsQ.data}
          initiatives={initiativesQ.data}
          subcommittees={settings.committees.filter((item) => actionsQ.manualActionSubcommitteeIds.includes(item.id))}
          defaultDistrictId={
            districtFilter !== 'All' ? districtFilter : (profile.districtId ?? settings.districts[0]?.id ?? '')
          }
          onClose={() => setCreating(false)}
          onSave={save}
        />
      )}

      {detail && (
        <ActionDrawer
          action={actionsQ.data.find((a) => a.id === detail.id) ?? detail}
          profile={profile}
          settings={settings}
          contacts={contactsQ.data}
          people={peopleQ.options}
          labelFor={(id) => labelFor(id)}
          units={unitsQ.data}
          goals={goalsQ.data}
          initiatives={initiativesQ.data}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}

function ActionDrawer({
  action,
  profile,
  settings,
  contacts,
  people,
  labelFor,
  units,
  goals,
  initiatives,
  onClose,
}: {
  action: Action;
  profile: UserProfile;
  settings: AppSettings;
  contacts: Contact[];
  people: PersonOption[];
  labelFor: (personId: string) => string | null;
  units: Unit[];
  goals: Goal[];
  initiatives: Initiative[];
  onClose: () => void;
}) {
  const email = profile.email.toLowerCase();
  const editable = canEditActionFully(profile, action);
  const canComment = canCommentOnAction(profile, action);
  const due = actionDueDateDisplay(action);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const toPerson = useMemo(() => contactToPersonFrom(contacts), [contacts]);
  const ownerPersonId = ownerPersonOf(action, toPerson) ?? '';
  const participantIds = participantPersonsOf(action, toPerson);
  const relatedPersonId = action.relatedPersonId ?? (action.relatedContactId ? toPerson(action.relatedContactId) : null);
  const name = (personId: string | null | undefined) => (personId ? (labelFor(personId) ?? 'Unknown') : null);

  const guard = async (fn: () => Promise<void>) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save that change.');
    } finally {
      setBusy(false);
    }
  };

  // completedAt is set on any transition INTO 'completed' and cleared on
  // any transition out of it (including cancelling, reopening, or picking
  // a different active status directly from the dropdown).
  const changeStatus = (status: ActionStatus) =>
    guard(async () => {
      const text =
        status === 'completed'
          ? ['marked the Action Completed.']
          : action.status === 'completed'
            ? ['reopened the Action.']
            : [`changed status from ${ACTION_STATUS_LABELS[action.status]} to ${ACTION_STATUS_LABELS[status]}.`];
      await updateActionWithActivity(
        action.id,
        { status, completedAt: status === 'completed' ? serverTimestamp() : null },
        text,
        email,
      );
    });

  const complete = () => changeStatus('completed');
  const reopen = () => changeStatus('open');

  const changePriority = (priority: typeof action.priority) =>
    guard(async () => {
      await updateActionWithActivity(
        action.id,
        { priority },
        [`changed priority from ${ACTION_PRIORITY_LABELS[action.priority]} to ${ACTION_PRIORITY_LABELS[priority]}.`],
        email,
      );
    });

  const changeDueDate = (dueDate: string | null) =>
    guard(async () => {
      const from = action.dueDate ? action.dueDate : 'none';
      const to = dueDate ? dueDate : 'none';
      await updateActionWithActivity(action.id, { dueDate }, [`changed due date from ${from} to ${to}.`], email);
    });

  const changeOwner = (personId: string) =>
    guard(async () => {
      if (!personId || personId === ownerPersonId) return;
      const from = name(ownerPersonId) ?? 'no one';
      const to = name(personId) ?? 'no one';
      await updateActionWithActivity(action.id, { ownerPersonId: personId }, [`reassigned owner from ${from} to ${to}.`], email);
    });

  const addParticipant = (personId: string) =>
    guard(async () => {
      await updateActionWithActivity(
        action.id,
        { participantPersonIds: [...participantIds, personId] },
        [`added ${name(personId)} as a participant.`],
        email,
      );
    });

  const removeParticipant = (personId: string) =>
    guard(async () => {
      await updateActionWithActivity(
        action.id,
        { participantPersonIds: participantIds.filter((id) => id !== personId) },
        [`removed ${name(personId)} as a participant.`],
        email,
      );
    });

  const addNote = async (text: string) => {
    await addActionNote(action.id, text, email);
  };

  return (
    <Drawer
      title={action.title}
      subtitle={
        <span className="subtitle">
          <RefBadge value={action.ref} /> {recName(settings.districts, action.districtId)}
          {action.source === 'system' && (
            <span className="action-system-badge" title="Created and maintained automatically by Compass">
              {' '}
              · Created by Compass · {workItemTypeLabel(actionTypeKey(action))}
            </span>
          )}
        </span>
      }
      onClose={onClose}
    >
      {err && <div className="err">{err}</div>}

      <div className="uh-drawer-block">
        <h4>Status &amp; priority</h4>
        {editable ? (
          <div style={{ display: 'flex', gap: 10 }}>
            <select value={action.status} disabled={busy} onChange={(e) => changeStatus(e.target.value as ActionStatus)}>
              {ACTION_STATUS_ORDER.map((s) => (
                <option key={s} value={s}>
                  {ACTION_STATUS_LABELS[s]}
                </option>
              ))}
            </select>
            <select
              value={action.priority}
              disabled={busy}
              onChange={(e) => changePriority(e.target.value as typeof action.priority)}
            >
              {ACTION_PRIORITY_ORDER.map((p) => (
                <option key={p} value={p}>
                  {ACTION_PRIORITY_LABELS[p]}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div>
            {ACTION_STATUS_LABELS[action.status]} · {ACTION_PRIORITY_LABELS[action.priority]}
          </div>
        )}
        {editable && !actionIsResolved(action.status) && (
          <button className="btn" style={{ marginTop: 8 }} disabled={busy} onClick={complete}>
            Mark Completed
          </button>
        )}
        {editable && actionIsResolved(action.status) && (
          <button className="btn" style={{ marginTop: 8 }} disabled={busy} onClick={reopen}>
            Reopen
          </button>
        )}
      </div>

      <div className="uh-drawer-block">
        <h4>Owner</h4>
        {editable ? (
          <PersonPicker
            options={people}
            districtId={action.districtId}
            value={ownerPersonId}
            onChange={(id) => { if (id) void changeOwner(id); }}
            exclude={participantIds}
            labelFor={labelFor}
            disabled={busy}
            placeholder="Search for a new owner…"
          />
        ) : (
          <div>{name(ownerPersonId) ?? 'Unassigned'}</div>
        )}
      </div>

      <div className="uh-drawer-block">
        <h4>Due date</h4>
        {editable ? (
          <input
            type="date"
            value={action.dueDate ?? ''}
            disabled={busy}
            onChange={(e) => changeDueDate(e.target.value || null)}
          />
        ) : (
          <div>
            {action.dueDate ? due.text : 'No due date'}
            {due.context && (
              <span
                className={`action-due-context${
                  actionIsOverdue(action) ? ' is-overdue' : actionIsDueSoon(action) ? ' is-soon' : ''
                }`}
              >
                {due.context}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="uh-drawer-block">
        <h4>Participants</h4>
        {participantIds.length === 0 && <div className="subtitle">No participants.</div>}
        {editable ? (
          <PersonMultiPicker
            options={people}
            districtId={action.districtId}
            values={participantIds}
            exclude={ownerPersonId ? [ownerPersonId] : []}
            onChange={(ids) => {
              const added = ids.find((id) => !participantIds.includes(id));
              const removed = participantIds.find((id) => !ids.includes(id));
              if (added) void addParticipant(added);
              else if (removed) void removeParticipant(removed);
            }}
            labelFor={labelFor}
            disabled={busy}
          />
        ) : (
          participantIds.length > 0 && (
            <div className="people-chips">
              {participantIds.map((id) => <span key={id} className="people-chip">{name(id) ?? 'Unknown'}</span>)}
            </div>
          )
        )}
      </div>

      {(action.description ||
        action.relatedUnitId ||
        action.relatedUnitIds?.length ||
        action.relatedGoalId ||
        action.relatedInitiativeId ||
        relatedPersonId) && (
        <div className="uh-drawer-block">
          <h4>Details</h4>
          {action.description && <div className="subtitle" style={{ marginBottom: 6 }}>{action.description}</div>}
          {(action.relatedUnitIds?.length ? action.relatedUnitIds : action.relatedUnitId ? [action.relatedUnitId] : []).length > 0 && (
            <div>
              {(action.relatedUnitIds?.length ? action.relatedUnitIds.length : 1) === 1 ? 'Unit: ' : 'Units: '}
              {(action.relatedUnitIds?.length ? action.relatedUnitIds : [action.relatedUnitId as string])
                .map((id) => units.find((u) => u.id === id)?.name ?? 'Unknown')
                .join(', ')}
            </div>
          )}
          {relatedPersonId && <div>Related person: {name(relatedPersonId)}</div>}
          {action.relatedGoalId && (
            <div>Goal: {goals.find((g) => g.id === action.relatedGoalId)?.title ?? 'Unknown'}</div>
          )}
          {action.relatedInitiativeId && (
            <div>Initiative: {initiatives.find((i) => i.id === action.relatedInitiativeId)?.name ?? 'Unknown'}</div>
          )}
        </div>
      )}

      <div className="uh-drawer-block">
        <h4>Created</h4>
        <div className="subtitle">
          By {action.createdBy.split('@')[0]}
          {action.source === 'system' ? ` · system (${workItemTypeLabel(actionTypeKey(action))})` : ''}
        </div>
      </div>

      <div className="uh-drawer-block">
        <h4>Activity &amp; Notes</h4>
        <ActionActivityFeed activity={action.activity} canAdd={canComment} onAddNote={addNote} />
      </div>
    </Drawer>
  );
}
